import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import Popup from "reactjs-popup";
import AsyncSelect from 'react-select/async';

const axios = require('axios').default;

const popupStyle = {
  "background": "var(--sl-bg-primary)",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "700px",
  "padding": "0px"
};

const TicketPreviewModal = ({
  open,
  onClose,
  teamId,
  confirmId
}) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const loadOptions = useCallback(() => {
    const ticketTypesPromise = axios.get(`/teams/${teamId}/confirms/${confirmId}/ticket_types.json`)
      .then(response => response.data)
      .catch(() => []);

    const addOnsPromise = axios.get(`/teams/${teamId}/confirms/${confirmId}/add_ons.json`)
      .then(response => response.data)
      .catch(() => []);

    return Promise.all([ticketTypesPromise, addOnsPromise]).then(([ticketTypes, addOns]) => {
      const groups = [];

      if (ticketTypes.length > 0) {
        groups.push({
          label: 'Ticket Types',
          options: ticketTypes.map((ticketType) => ({
            value: ticketType.id,
            label: `${ticketType.name} - $${ticketType.price_two_decimals || '0.00'}`,
            type: 'ticket_type'
          }))
        });
      }

      if (addOns.length > 0) {
        groups.push({
          label: 'Add-Ons',
          options: addOns.map((addOn) => ({
            value: addOn.id,
            label: `${addOn.name} - $${addOn.price_two_decimals || '0.00'}`,
            type: 'add_on'
          }))
        });
      }

      return groups;
    });
  }, [teamId, confirmId]);

  const handleTicketTypeChange = (option) => {
    setSelectedOption(option);
    if (option) {
      setLoadingPreview(true);
    }
  };

  const handleIframeLoad = () => {
    setLoadingPreview(false);
  };

  const handleClose = () => {
    setSelectedOption(null);
    setLoadingPreview(false);
    onClose();
  };

  const previewUrl = selectedOption
    ? `/teams/${teamId}/confirms/${confirmId}/ticket_preview?${selectedOption.type === 'ticket_type' ? 'ticket_type_id' : 'add_on_id'}=${selectedOption.value}`
    : null;

  return (
    <Popup
      open={open}
      contentStyle={popupStyle}
      closeOnDocumentClick={true}
      className="popup-modal"
      onClose={handleClose}
    >
      <div className="popup-modal-container">
        <div className="row m-0" style={{ borderBottom: "1px solid var(--od-c-f3f3f3, #f2f2f2)" }}>
          <div className="col d-flex" style={{ padding: "17px 24px" }}>
            <h3 className="mb-0 align-self-center" style={{ fontSize: "20px" }}>
              <strong>
                <i className="far fa-ticket text-primary mr-2"></i>
                View Ticket Header
              </strong>
            </h3>
          </div>
          <div className="col-xs-auto d-flex align-items-center" style={{ paddingRight: "24px" }}>
            <a
              href="#"
              style={{ fontSize: "20px" }}
              className="text-dark"
              onClick={(e) => {
                e.preventDefault();
                handleClose();
              }}
            >
              <i className="fal fa-times"></i>
            </a>
          </div>
        </div>

        <div className="row m-0">
          <div className="col-12" style={{ padding: "18px 24px 24px 24px" }}>
            <div style={{ padding: '2px' }}>
              <label htmlFor="ticket-type-select" className="form-label" style={{ fontSize: '14px' }}>
                Select a ticket:
              </label>
              <AsyncSelect
                id="ticket-type-select"
                placeholder="Select a ticket..."
                loadOptions={loadOptions}
                defaultOptions
                value={selectedOption}
                onChange={handleTicketTypeChange}
                isClearable={false}
                menuPortalTarget={document.body}
                cacheOptions
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({ ...base, fontSize: '15px' }),
                  menu: (base) => ({ ...base, fontSize: '15px' })
                }}
                noOptionsMessage={() => "No tickets or add-ons available"}
              />
            </div>

            {selectedOption && (
              <div style={{ marginTop: '16px' }}>
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '204px',
                    border: '1px solid var(--od-c-ececec, #ececec)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    backgroundColor: 'var(--od-c-f5f5f5, #f5f5f5)'
                  }}
                >
                  {loadingPreview && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'var(--od-c-f5f5f5, #f5f5f5)',
                        zIndex: 1
                      }}
                    >
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Loading preview...
                    </div>
                  )}
                  <iframe
                    src={previewUrl}
                    onLoad={handleIframeLoad}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none'
                    }}
                    title="Ticket Preview"
                  />
                </div>
              </div>
            )}

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              {selectedOption && !loadingPreview && (
                <a
                  href={`${previewUrl}&download=true`}
                  className="text-primary mr-3"
                  style={{ fontSize: "14px" }}
                >
                  <strong>Download</strong>
                </a>
              )}
              <button
                type="button"
                style={{ borderRadius: "8px", fontSize: "14px" }}
                className="btn btn-primary"
                onClick={handleClose}
              >
                <strong>Close</strong>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

TicketPreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  confirmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default TicketPreviewModal;
